/**
 * One row of the model tree, and what can be done to it.
 *
 * Both names on every row, per the app's convention: the declared name reads as
 * prose and the <angle-bracket> short name is the address, and a screen that
 * shows only one of them makes the model impossible to talk about.
 *
 * Every action appears on every row and is DISABLED with its reason rather than
 * hidden. That matters more here than anywhere else in BEST: this tree shows the
 * whole file, including library elements and elements BEST does not project, so
 * "why can I not edit this one?" is a question people will actually have - and a
 * menu that quietly grows and shrinks per row answers it with a shrug.
 */

import { useState } from 'react'
import type { MouseEvent } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import type { TreeAction, TreeNode, WritePermission } from './tree-model'
import { refusalFor } from './tree-model'
import { MONOSPACE } from '../../theme'

const LABELS: Record<TreeAction, string> = {
  reveal: 'Open its page',
  source: 'Show source',
  new: 'New inside…',
  rename: 'Rename',
  duplicate: 'Duplicate',
  delete: 'Delete',
}

export default function ModelTreeBranch({
  node,
  write,
  onAction,
}: {
  node: TreeNode
  write: WritePermission
  onAction: (action: TreeAction, node: TreeNode) => void
}) {
  const { row } = node
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const label = row.id || row.name || row.qualified_name.split('::').at(-1) || '(unnamed)'

  const open = (event: MouseEvent<HTMLElement>) => {
    // Otherwise the tree item collapses under the menu that just opened.
    event.stopPropagation()
    setAnchor(event.currentTarget)
  }

  const choose = (action: TreeAction) => {
    setAnchor(null)
    onAction(action, node)
  }

  /** A menu entry, live or refused - never absent. §5.1. */
  const entry = (action: TreeAction) => {
    const refusal = refusalFor(action, node, write)
    const item = (
      <MenuItem disabled={Boolean(refusal)} onClick={() => choose(action)}>
        {LABELS[action]}
      </MenuItem>
    )
    return (
      <Tooltip key={action} title={refusal} describeChild placement="left">
        {refusal ? <span>{item}</span> : item}
      </Tooltip>
    )
  }

  const revealable = !refusalFor('reveal', node, write)

  return (
    <TreeItem
      itemId={node.key}
      label={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
          <Chip
            label={row.kind}
            size="small"
            variant="outlined"
            color={row.kind === 'package' ? 'primary' : 'default'}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />

          {revealable ? (
            <RouterLink
              to={`/${row.plural}/${encodeURIComponent(row.id)}`}
              style={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}
              onClick={(event) => event.stopPropagation()}
            >
              {label}
            </RouterLink>
          ) : (
            <Typography sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>{label}</Typography>
          )}

          {row.name && row.name !== label && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {row.name}
            </Typography>
          )}

          {row.type_name && (
            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: MONOSPACE }}>
              : {row.type_name}
            </Typography>
          )}

          {node.paths.length > 1 && (
            <Tooltip
              title={
                <>
                  Declared in {node.paths.length} files, shown once:
                  {node.paths.map((path) => (
                    <div key={path} style={{ fontFamily: MONOSPACE }}>
                      {path}
                    </div>
                  ))}
                </>
              }
            >
              <Chip
                label={`${node.paths.length} files`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Tooltip>
          )}

          {row.is_library && (
            <Chip label="library" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
          )}

          {!row.plural && row.kind !== 'package' && (
            <Tooltip title="BEST stores this element but does not project it, so it has no fields to edit.">
              <Chip
                label="not projected"
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Tooltip>
          )}

          <span style={{ flexGrow: 1 }} />

          <IconButton
            size="small"
            aria-label={`Actions for ${label}`}
            onClick={open}
            sx={{ p: 0.25 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          {/* In the LABEL, not among the children: a Menu passed as a child
              would give a leaf row an expand arrow that opens onto nothing.
              It is a portal either way, so where it is declared is free. */}
          {anchor && (
            <Menu open anchorEl={anchor} onClose={() => setAnchor(null)}>
              {entry('reveal')}
              {entry('source')}
              <Divider />
              {entry('new')}
              {entry('rename')}
              {entry('duplicate')}
              {entry('delete')}
            </Menu>
          )}
        </Stack>
      }
    >
      {node.children.map((child) => (
        <ModelTreeBranch key={child.key} node={child} write={write} onAction={onAction} />
      ))}
    </TreeItem>
  )
}
